// ABOUTME: Chat input component with text area, voice notes, image and video
// attachment (camera capture, paperclip, paste; drops go through FileDropZone), and one right-hand
// action: Send when there is something to send, otherwise the live-conversation button.
// A recorded voice note waits above the composer, playable with its transcript, until the owner
// sends or deletes it.

import { useCallback } from 'react'
import { useAskAbout } from '@/lib/askAbout'
import {
  ArrowUp,
  Plus,
  Mic,
  MicOff,
  AlertCircle,
  Camera,
  Square,
  Trash2,
  X,
  FileAudio,
  FileImage,
  FileText,
  FileVideo,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ContextChip } from '@/components/ui/context-chip'
import { ComposerButton, PillComposer } from '@/components/ui/pill-composer'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  type VoiceNoteResult,
  type VoiceRecorderError,
  type VoiceRecorderStatus,
} from '@/hooks/useVoiceRecorder'
import { ATTACHMENT_ACCEPT, type PendingFile } from '@/hooks/useImageUpload'
import { LiveConversationButton } from '@/components/chat/LiveConversationButton'
import { UPLOAD_STATUS_LABELS, useMediaUploadStore } from '@/stores/useMediaUploadStore'
import { useKeyboardOpen } from '@/hooks/useKeyboardOpen'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { pickMediaFiles } from '@/platform'

interface ChatInputProps {
  inputValue: string
  onInputChange: (value: string) => void
  onSubmit: (e: React.FormEvent) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  /** Recording a voice note right now (mic held on mobile / toggled on web). */
  isRecording: boolean
  voiceRecorderStatus: VoiceRecorderStatus
  voiceRecorderError: VoiceRecorderError | null
  isVoiceRecorderSupported: boolean
  /** Start recording. Mobile: fired on press (hold-to-record). Web: fired on click (toggle). */
  onVoiceStart: () => void
  /** Stop, upload and transcribe. Mobile: fired on release. Web: fired on the second click. */
  onVoiceStop: () => void
  /** The recorded note waiting for the owner to send or delete it. */
  voiceNote: VoiceNoteResult | null
  onVoiceNoteSend: () => void
  onVoiceNoteDiscard: () => void
  /** Open the live (spoken) conversation with the agent. */
  onStartLive: () => void
  inputRef: React.RefObject<HTMLTextAreaElement | null>
  pendingCount?: number
  // Image attachment props
  pendingFiles: PendingFile[]
  /** Files still being compressed — one placeholder tile each, and send is held. */
  compressingCount: number
  onFilesAdd: (files: FileList | File[]) => void
  onFileRemove: (id: string) => void
  onAttachClick: () => void
  fileInputRef: React.RefObject<HTMLInputElement | null>
  /** The team member the next message answers (its chip), when it is one. */
  replyTo?: { name: string; onRemove: () => void } | null
}

/** A named tile's icon: what kind of file it is. */
function AttachmentIcon({ kind }: { kind: PendingFile['kind'] }) {
  if (kind === 'video') return <FileVideo className="size-5 shrink-0" />
  if (kind === 'audio') return <FileAudio className="size-5 shrink-0" />
  if (kind === 'image') return <FileImage className="size-5 shrink-0" />
  return <FileText className="size-5 shrink-0" />
}

/** Where a large file is in the resumable upload, which the message waits for; nothing once
 * it is in the Library. */
function UploadProgress({ resumableId }: { resumableId: string }) {
  const item = useMediaUploadStore((s) => s.uploads[resumableId])
  if (item?.status === 'done') return null
  const status = item?.status ?? 'uploading'
  return (
    <span className="text-[11px] tabular-nums" data-testid="attachment-upload-progress">
      {status === 'uploading'
        ? `${UPLOAD_STATUS_LABELS.uploading} ${item?.progress ?? 0}%`
        : UPLOAD_STATUS_LABELS[status]}
    </span>
  )
}

export function ChatInput({
  inputValue,
  onInputChange,
  onSubmit,
  onKeyDown,
  isRecording,
  voiceRecorderStatus,
  voiceRecorderError,
  isVoiceRecorderSupported,
  onVoiceStart,
  onVoiceStop,
  voiceNote,
  onVoiceNoteSend,
  onVoiceNoteDiscard,
  onStartLive,
  inputRef,
  pendingCount = 0,
  pendingFiles,
  compressingCount,
  onFilesAdd,
  onFileRemove,
  onAttachClick,
  fileInputRef,
  replyTo,
}: ChatInputProps) {
  const isTouchDevice = useMediaQuery('(pointer: coarse)')
  const keyboardOpen = useKeyboardOpen()
  const { askAbout, clear: clearAskAbout } = useAskAbout()

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const files = e.clipboardData?.files
      if (files && files.length > 0) {
        // Any file: pasting a clip or a document is the same gesture as pasting a screenshot.
        e.preventDefault()
        onFilesAdd(Array.from(files))
      }
    },
    [onFilesAdd]
  )

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) {
        onFilesAdd(files)
      }
      // Reset input so the same file can be selected again
      e.target.value = ''
    },
    [onFilesAdd]
  )

  // Camera capture. `capture: 'environment'` opens the rear camera directly on
  // a phone; desktop browsers ignore it, so the button is touch-only.
  const handleTakePhoto = useCallback(async () => {
    const files = await pickMediaFiles({ accept: 'image/*', capture: 'environment' })
    if (files.length > 0) {
      onFilesAdd(files)
    }
  }, [onFilesAdd])

  // Hold-to-record on mobile (press starts, release stops for review); click to
  // toggle on web (mouse pointers rarely stay held for a whole sentence).
  // Touch also fires a synthetic click after pointerup, so the click handler
  // is wired only for non-touch pointers — never both — to avoid a second
  // start/stop toggling right back.
  const handleMicPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!isTouchDevice) return
      e.preventDefault()
      onVoiceStart()
    },
    [isTouchDevice, onVoiceStart]
  )
  const handleMicPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!isTouchDevice) return
      e.preventDefault()
      onVoiceStop()
    },
    [isTouchDevice, onVoiceStop]
  )
  const handleMicClick = useCallback(() => {
    if (isTouchDevice) return
    if (isRecording) onVoiceStop()
    else onVoiceStart()
  }, [isTouchDevice, isRecording, onVoiceStart, onVoiceStop])

  const hasText = inputValue.trim().length > 0
  const hasContent = hasText || pendingFiles.length > 0
  const isCompressing = compressingCount > 0
  const isTranscribing = voiceRecorderStatus === 'transcribing'

  return (
    <div className={cn('px-4 pt-2 pb-4', keyboardOpen && 'pb-2')}>
      {/* Recording / transcribing indicator */}
      {(isRecording || voiceRecorderStatus === 'transcribing') && (
        <div
          className="bg-destructive/10 text-destructive mb-2 flex items-center justify-center gap-2 rounded-md px-3 py-2"
          data-testid="voice-recording-indicator"
        >
          <span className="bg-destructive inline-flex size-2 shrink-0 animate-pulse rounded-full" />
          <span className="text-sm font-medium">
            {isRecording
              ? isTouchDevice
                ? 'Recording… release to stop'
                : 'Recording… tap to stop'
              : 'Transcribing…'}
          </span>
        </div>
      )}

      {voiceNote && (
        <div
          className="bg-muted mb-2 flex flex-col gap-2 rounded-xl p-3 sm:flex-row sm:items-center"
          data-testid="voice-note-review"
        >
          <div className="min-w-0 flex-1 space-y-1.5">
            <audio
              controls
              src={voiceNote.attachment.url}
              className="h-9 w-full max-w-xs"
              data-testid="voice-note-audio"
            />
            <p
              className="text-muted-foreground truncate text-sm"
              data-testid="voice-note-transcript"
            >
              {voiceNote.transcript.trim() || 'No words heard'}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <ComposerButton
              tone="subtle"
              type="button"
              onClick={onVoiceNoteDiscard}
              aria-label="Delete voice note"
              data-testid="voice-note-delete"
            >
              <Trash2 />
            </ComposerButton>
            <ComposerButton
              tone="solid"
              type="button"
              onClick={onVoiceNoteSend}
              aria-label="Send voice note"
              data-testid="voice-note-send"
            >
              <ArrowUp />
            </ComposerButton>
          </div>
        </div>
      )}

      {/* Voice error message */}
      {voiceRecorderError && !isRecording && (
        <div
          className="bg-destructive/10 text-destructive mb-2 flex items-center gap-2 rounded-md px-3 py-2"
          data-testid="voice-error-message"
        >
          <AlertCircle className="size-4 shrink-0" />
          <span className="text-sm">{voiceRecorderError.message}</span>
        </div>
      )}

      {/* Attachment preview strip */}
      {(pendingFiles.length > 0 || isCompressing) && (
        <div className="mb-2 flex flex-wrap gap-2" data-testid="attachment-preview-strip">
          {pendingFiles.map((pf) => (
            <div key={pf.id} className="group/thumb relative">
              {!pf.preview ? (
                // No poster frame: we never decode the file to render a
                // thumbnail. A named affordance is the honest shape.
                <div
                  className={cn(
                    'bg-muted text-muted-foreground flex h-14 max-w-40 items-center gap-2 rounded-md border px-2.5',
                    pf.uploading && !pf.resumableId && 'opacity-50',
                    pf.error && 'border-destructive opacity-60'
                  )}
                  data-testid={`attachment-${pf.kind}-tile`}
                  title={pf.filename}
                >
                  <AttachmentIcon kind={pf.kind} />
                  <span className="flex min-w-0 flex-col">
                    <span className="text-foreground truncate text-xs">{pf.filename}</span>
                    {pf.resumableId && !pf.uploadId && !pf.error && (
                      <UploadProgress resumableId={pf.resumableId} />
                    )}
                  </span>
                </div>
              ) : (
                <img
                  src={pf.preview}
                  alt={pf.filename}
                  className={cn(
                    'size-14 rounded-md border object-cover',
                    pf.uploading && 'opacity-50',
                    pf.error && 'border-destructive opacity-60'
                  )}
                />
              )}
              {pf.uploading && !pf.resumableId && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-background/80 size-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                </div>
              )}
              <button
                type="button"
                onClick={() => onFileRemove(pf.id)}
                className="tap-target absolute -top-3 -right-3 flex size-[44px] items-center justify-center md:-top-2 md:-right-2 md:size-8"
                aria-label={`Remove ${pf.filename}`}
              >
                <span className="bg-foreground/70 text-background flex size-5 items-center justify-center rounded-full">
                  <X className="size-3" />
                </span>
              </button>
            </div>
          ))}
          {/* Placeholder tile per photo still compressing */}
          {Array.from({ length: compressingCount }, (_, i) => (
            <div
              key={`compressing-${i}`}
              className="bg-muted text-muted-foreground flex size-14 items-center justify-center rounded-md border"
              data-testid="attachment-compressing"
              aria-label="Compressing photo"
            >
              <div className="size-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
            </div>
          ))}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ATTACHMENT_ACCEPT}
        multiple
        className="hidden"
        onChange={handleFileInputChange}
        data-testid="file-input"
      />

      <form onSubmit={onSubmit} data-testid="chat-input-form">
        <PillComposer
          context={
            replyTo ? (
              <ContextChip
                label={`Replying to ${replyTo.name}`}
                onRemove={replyTo.onRemove}
                data-testid="member-reply-chip"
              />
            ) : (
              askAbout && <ContextChip label={askAbout.label} onRemove={clearAskAbout} />
            )
          }
          leading={
            <>
              {/* Take photo: the primary attach affordance on a phone. Desktop
                  browsers ignore the capture hint, so it only renders on touch. */}
              {isTouchDevice && (
                <ComposerButton
                  tone="subtle"
                  onClick={handleTakePhoto}
                  disabled={isRecording}
                  aria-label="Take photo"
                  data-testid="take-photo-button"
                >
                  <Camera />
                </ComposerButton>
              )}
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <ComposerButton
                    tone="subtle"
                    onClick={onAttachClick}
                    disabled={isRecording}
                    aria-label="Attach a photo, video, recording or document"
                    data-testid="attach-image-button"
                  >
                    <Plus />
                  </ComposerButton>
                </TooltipTrigger>
                <TooltipContent side="top">
                  Attach a photo, video, recording or document
                </TooltipContent>
              </Tooltip>
            </>
          }
          trailing={
            <>
              {/* Microphone (a voice note): hold to record on mobile, click to toggle on web
                  (see handleMic* above). Pointer leave/cancel also stop a held recording so
                  dragging off the button doesn't strand it. It never moves while recording (the
                  right-hand action beside it always stays), so a held finger stays on it. One
                  note waits at a time: send or delete it before recording another. */}
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <ComposerButton
                    tone={isRecording ? 'solid' : 'subtle'}
                    onPointerDown={handleMicPointerDown}
                    onPointerUp={handleMicPointerUp}
                    onPointerLeave={handleMicPointerUp}
                    onPointerCancel={handleMicPointerUp}
                    onClick={handleMicClick}
                    disabled={!isVoiceRecorderSupported || isTranscribing || !!voiceNote}
                    aria-label={isRecording ? 'Stop recording' : 'Record a voice note'}
                    data-testid="voice-input-button"
                    className={cn(
                      isRecording &&
                        'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                    )}
                  >
                    {!isVoiceRecorderSupported ? (
                      <MicOff />
                    ) : isRecording ? (
                      <Square className="fill-current" />
                    ) : (
                      <Mic />
                    )}
                  </ComposerButton>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {!isVoiceRecorderSupported
                    ? 'Voice notes not supported'
                    : voiceNote
                      ? 'Send or delete the voice note first'
                      : isRecording
                        ? 'Release to stop'
                        : isTouchDevice
                          ? 'Hold to record a voice note'
                          : 'Record a voice note'}
                </TooltipContent>
              </Tooltip>

              {/* Send stays available while a run is in flight: a message sent then
                  joins that turn, or the next one if it has just ended. With nothing to
                  send, the same place starts a live conversation. */}
              {hasContent ? (
                <ComposerButton
                  tone="solid"
                  type="submit"
                  disabled={isCompressing || isRecording}
                  aria-label="Send message"
                  data-testid="chat-send-button"
                >
                  <ArrowUp />
                </ComposerButton>
              ) : (
                <LiveConversationButton onClick={onStartLive} disabled={isRecording} />
              )}
            </>
          }
        >
          <Textarea
            ref={inputRef}
            placeholder="Message"
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={handlePaste}
            className="max-h-40 min-h-11 resize-none rounded-none border-0 bg-transparent px-1 py-[11px] text-[0.9375rem] leading-[22px] shadow-none focus-visible:ring-0 md:text-[0.9375rem] dark:bg-transparent"
            rows={1}
            data-testid="chat-input"
          />
        </PillComposer>
      </form>
      {keyboardOpen ? null : pendingCount > 0 ? (
        <p className="text-muted-foreground mt-2 text-center text-xs" data-testid="queue-indicator">
          Waiting on {pendingCount} repl{pendingCount > 1 ? 'ies' : 'y'}
        </p>
      ) : (
        !isTouchDevice && (
          <p className="text-muted-foreground mt-2 text-center text-xs" data-testid="keyboard-hint">
            Press Enter to send, Shift+Enter for new line
          </p>
        )
      )}
    </div>
  )
}
